import { useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import TableManager from '../TableManager/TableManager';
import { COURSE_ASSIGNMENT_INDIVIDUALS_HEADINGS } from '../../../../lib/constants/courses';
import { adaptUserProps } from './CourseIndividuals.utils';
import { ASSIGN_COURSE_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { CourseIndividualsProps } from './CourseIndividuals.types';
import styles from './CourseIndividuals.module.scss';
import { adaptArrayToTableObject, baseSearchFn, TrainingStatusKey } from '../TableManager/TableManager.utils';
import { ConfirmDialog } from '../../../common/ConfirmDialog/ConfirmDialog';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../Notification/Notification';
import { useTrainingUsers } from '../../../../lib/api/swrApi';
import { isNil } from '../../../../lib/utils/isNil';
import { useDeleteUserFromTraining } from '../../../../lib/api/postApi';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const CourseIndividuals = ({
  courseDetails,
  pageSize,
  showPageSizeSelector,
  pageSizeOptions,
  range,
  removeUserSuccessMessage,
  itemsDeleteState,
  itemsDeleteStateDispatch,
  type,
}: CourseIndividualsProps) => {
  const { id } = courseDetails;
  const { mutate } = useSWRConfig();
  const { data, mutate: mutateUsers, isLoading, isValidating, error } = useTrainingUsers(id, type, range);
  const { triggerSnackbar } = useSnackbarContext();
  const fetchDeleteUser = useDeleteUserFromTraining(type);
  const [userDialogId, setUserDialogId] = useState<string | null>(null);
  const { triggerSnackbarError } = useErrorMapping();
  const tableHeading = COURSE_ASSIGNMENT_INDIVIDUALS_HEADINGS.slice(0, -1);

  const onClickBinHandler = (userId: string) => {
    setUserDialogId(userId);
  };

  const deleteUser = () => {
    if (!userDialogId) {
      return;
    }

    itemsDeleteStateDispatch({ type: 'deleteStateAdd', payload: userDialogId });
    fetchDeleteUser({ userid: userDialogId, courseid: id, lpId: id })
      .then(() => {
        triggerSnackbar(removeUserSuccessMessage, NotificationType.Success);
        mutateUsers();
        mutate(([key]: string[]) => key.endsWith('/users'));
      })
      .catch(err => {
        triggerSnackbarError(err);
      })
      .finally(() => itemsDeleteStateDispatch({ type: 'deleteStateRemove', payload: userDialogId }));
    setUserDialogId(null);
  };

  const tableData = useMemo(
    () =>
      data
        ?.map(adaptUserProps(itemsDeleteState)(onClickBinHandler))
        .reduce(adaptArrayToTableObject(COURSE_ASSIGNMENT_INDIVIDUALS_HEADINGS), {}),
    [type, data, itemsDeleteState],
  );

  const selected = tableData
    ? Object.entries(tableData)
        .filter(
          ([, rowContent]) => typeof rowContent[2] === 'object' && rowContent[2].key === TrainingStatusKey.OVERDUE,
        )
        .map(([rowId]) => rowId)
    : [];

  return (
    <ErrorBoundary error={error} internal>
      <TableManager
        className={styles.centeredOverdue}
        headings={tableHeading}
        pageSize={pageSize}
        showPageSizeSelector={showPageSizeSelector}
        pageSizeOptions={pageSizeOptions}
        data={tableData ?? {}}
        selected={selected}
        searchBy={baseSearchFn(0)}
        searchPlaceholderKey={COMMON_LABELS.SEARCH.USER}
        isInitialLoad={isLoading}
        isLoading={isValidating && !isNil(data)}
      />
      <ConfirmDialog
        onCancel={() => setUserDialogId(null)}
        onConfirm={deleteUser}
        labelKeys={{
          dialogTitle: ASSIGN_COURSE_LABELS.CONFIRM_USER_DIALOG_TITLE,
          content: ASSIGN_COURSE_LABELS.CONFIRM_USER_DIALOG_CONTENT,
          confirmButton: ASSIGN_COURSE_LABELS.CONFIRM_DIALOG_CONFIRM_BUTTON,
        }}
        isOpen={!!userDialogId}
      />
    </ErrorBoundary>
  );
};

export default CourseIndividuals;
